/// <reference path="../types/sqljs.d.ts" />
import initSqlJs, { Database } from 'sql.js';
import wasmUrl from 'sql.js/dist/sql-wasm.wasm?url';
import {
  AUCTIONS,
  AUCTION_BIDS,
  CREDIT_FACILITIES,
  FACILITY_REQUIREMENTS,
  IOU_EVENTS,
  IOUS,
  KYC_CASES,
  LENDER_POSITIONS,
  LENDING_POOLS,
  LIQUIDATIONS,
  MARGIN_ACCOUNTS,
  MARGIN_ACCOUNT_COLLATERAL,
  ONCHAIN_CLAIMS,
  ONCHAIN_CLAIM_REVIEW_CASES,
  ONCHAIN_IDS,
  USER_FACILITY_ACCESS,
  USERS,
  YIELD_HISTORY,
  CF_MODULE_WHITELIST,
  CF_FACILITY_MODULES,
} from './seed';

let dbInstance: Database | null = null;
let initPromise: Promise<Database> | null = null;

function createSchema(db: Database): void {
  db.exec(`
    PRAGMA foreign_keys = ON;

    CREATE TABLE credit_facilities (
      id TEXT PRIMARY KEY,
      name TEXT NOT NULL,
      status TEXT NOT NULL,
      region TEXT NOT NULL,
      category TEXT NOT NULL,
      settlement_tokens TEXT NOT NULL,
      collateral_tokens TEXT NOT NULL,
      interest_rate_pct REAL NOT NULL,
      max_ltv_pct REAL NOT NULL,
      liquidation_threshold_pct REAL NOT NULL,
      utilization_pct REAL NOT NULL,
      tvl_usd INTEGER NOT NULL,
      borrowed_usd INTEGER NOT NULL,
      has_ddf INTEGER NOT NULL DEFAULT 0
    );

    CREATE TABLE users (
      id TEXT PRIMARY KEY,
      name TEXT NOT NULL,
      wallet TEXT NOT NULL,
      email TEXT NOT NULL,
      investor_type TEXT NOT NULL,
      jurisdiction TEXT NOT NULL,
      kyc_status TEXT NOT NULL
    );

    CREATE TABLE margin_accounts (
      id TEXT PRIMARY KEY,
      facility_id TEXT NOT NULL REFERENCES credit_facilities(id),
      user_id TEXT NOT NULL REFERENCES users(id),
      wallet TEXT NOT NULL,
      status TEXT NOT NULL,
      settlement_token TEXT NOT NULL,
      collateral_value_usd INTEGER NOT NULL,
      borrowed_usd INTEGER NOT NULL,
      interest_accrued REAL NOT NULL,
      health_factor REAL NOT NULL,
      last_payment TEXT NOT NULL,
      next_payment TEXT NOT NULL
    );

    CREATE TABLE margin_account_collateral (
      id TEXT PRIMARY KEY,
      margin_account_id TEXT NOT NULL REFERENCES margin_accounts(id),
      token_symbol TEXT NOT NULL,
      amount REAL NOT NULL
    );

    CREATE TABLE kyc_cases (
      id TEXT PRIMARY KEY,
      user_id TEXT NOT NULL REFERENCES users(id),
      facility_id TEXT NOT NULL REFERENCES credit_facilities(id),
      provider TEXT NOT NULL,
      submitted_date TEXT NOT NULL,
      risk_score INTEGER NOT NULL,
      review_status TEXT NOT NULL
    );

    CREATE TABLE liquidations (
      id TEXT PRIMARY KEY,
      facility_id TEXT NOT NULL REFERENCES credit_facilities(id),
      margin_account_id TEXT NOT NULL REFERENCES margin_accounts(id),
      wallet TEXT NOT NULL,
      workflow_status TEXT NOT NULL,
      debt_amount_usd INTEGER NOT NULL,
      collateral_amount_usd INTEGER NOT NULL,
      health_factor REAL NOT NULL,
      time_in_default TEXT NOT NULL,
      executed_at TEXT,
      recovery_rate_pct REAL NOT NULL
    );

    CREATE TABLE auctions (
      id TEXT PRIMARY KEY,
      facility_id TEXT NOT NULL REFERENCES credit_facilities(id),
      margin_account_id TEXT NOT NULL REFERENCES margin_accounts(id),
      title TEXT NOT NULL,
      status TEXT NOT NULL,
      token_symbol TEXT NOT NULL,
      token_name TEXT NOT NULL,
      collateral_amount REAL NOT NULL,
      collateral_value_usd INTEGER NOT NULL,
      debt_amount_usd INTEGER NOT NULL,
      reserve_price_usd INTEGER NOT NULL,
      current_bid_usd INTEGER NOT NULL,
      bid_count INTEGER NOT NULL,
      time_remaining TEXT NOT NULL,
      settlement_token TEXT NOT NULL
    );

    CREATE TABLE auction_bids (
      id TEXT PRIMARY KEY,
      auction_id TEXT NOT NULL REFERENCES auctions(id),
      bidder_wallet TEXT NOT NULL,
      amount_usd INTEGER NOT NULL,
      timestamp TEXT NOT NULL,
      tx_hash TEXT NOT NULL,
      is_leading INTEGER NOT NULL
    );

    CREATE TABLE facility_requirements (
      id TEXT PRIMARY KEY,
      facility_id TEXT NOT NULL REFERENCES credit_facilities(id),
      requirement_type TEXT NOT NULL,
      claim_type TEXT NOT NULL,
      description TEXT NOT NULL
    );

    CREATE TABLE onchain_ids (
      id TEXT PRIMARY KEY,
      user_id TEXT NOT NULL REFERENCES users(id),
      address TEXT NOT NULL,
      source TEXT NOT NULL
    );

    CREATE TABLE onchain_claims (
      id TEXT PRIMARY KEY,
      onchain_id TEXT NOT NULL REFERENCES onchain_ids(id),
      claim_type TEXT NOT NULL,
      issuer TEXT NOT NULL,
      value TEXT NOT NULL,
      issued_at TEXT NOT NULL
    );

    CREATE TABLE onchain_claim_review_cases (
      id TEXT PRIMARY KEY,
      facility_id TEXT NOT NULL REFERENCES credit_facilities(id),
      user_id TEXT NOT NULL REFERENCES users(id),
      onchain_id TEXT NOT NULL REFERENCES onchain_ids(id),
      claim_type TEXT NOT NULL,
      provider TEXT NOT NULL,
      issuer TEXT NOT NULL,
      jurisdiction TEXT NOT NULL,
      submitted_date TEXT NOT NULL,
      risk_score INTEGER NOT NULL,
      review_status TEXT NOT NULL
    );

    CREATE TABLE user_facility_access (
      user_id TEXT NOT NULL REFERENCES users(id),
      facility_id TEXT NOT NULL REFERENCES credit_facilities(id),
      PRIMARY KEY (user_id, facility_id)
    );

    CREATE TABLE lending_pools (
      id TEXT PRIMARY KEY,
      facility_id TEXT NOT NULL REFERENCES credit_facilities(id),
      name TEXT NOT NULL,
      tvl_usd INTEGER NOT NULL,
      utilization_pct REAL NOT NULL,
      apy_pct REAL NOT NULL
    );

    CREATE TABLE lender_positions (
      id TEXT PRIMARY KEY,
      pool_id TEXT NOT NULL REFERENCES lending_pools(id),
      user_id TEXT NOT NULL REFERENCES users(id),
      deposited_usd INTEGER NOT NULL,
      lp_tokens INTEGER NOT NULL,
      earned_yield_usd INTEGER NOT NULL,
      withdrawable_pct REAL NOT NULL
    );

    CREATE TABLE ious (
      id TEXT PRIMARY KEY,
      pool_id TEXT NOT NULL REFERENCES lending_pools(id),
      facility_name TEXT NOT NULL,
      original_amount_usd INTEGER NOT NULL,
      repaid_amount_usd INTEGER NOT NULL,
      remaining_amount_usd INTEGER NOT NULL,
      status TEXT NOT NULL
    );

    CREATE TABLE iou_events (
      id TEXT PRIMARY KEY,
      iou_id TEXT NOT NULL REFERENCES ious(id),
      pool_id TEXT NOT NULL REFERENCES lending_pools(id),
      event_type TEXT NOT NULL,
      amount_usd INTEGER NOT NULL,
      created_at TEXT NOT NULL
    );

    CREATE TABLE yield_history (
      id TEXT PRIMARY KEY,
      pool_id TEXT NOT NULL REFERENCES lending_pools(id),
      user_id TEXT NOT NULL REFERENCES users(id),
      week_label TEXT NOT NULL,
      yield_usd INTEGER NOT NULL,
      cumulative_usd INTEGER NOT NULL
    );

    CREATE TABLE credit_facility_modules (
      id TEXT PRIMARY KEY,
      module_type TEXT NOT NULL,
      name TEXT NOT NULL,
      description TEXT NOT NULL,
      chain_address TEXT NOT NULL,
      chain_id TEXT NOT NULL,
      updated_at TEXT NOT NULL
    );

    CREATE TABLE credit_facilities_to_modules (
      id TEXT PRIMARY KEY,
      facility_id TEXT NOT NULL REFERENCES credit_facilities(id),
      module_id TEXT NOT NULL REFERENCES credit_facility_modules(id)
    );
  `);
}

function seedData(db: Database): void {
  const facilityStmt = db.prepare(`
    INSERT INTO credit_facilities (
      id, name, status, region, category, settlement_tokens, collateral_tokens,
      interest_rate_pct, max_ltv_pct, liquidation_threshold_pct, utilization_pct,
      tvl_usd, borrowed_usd, has_ddf
    ) VALUES (?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?)
  `);
  CREDIT_FACILITIES.forEach((item) => {
    facilityStmt.run([
      item.id,
      item.name,
      item.status,
      item.region,
      item.category,
      JSON.stringify(item.settlementTokens),
      JSON.stringify(item.collateralTokens),
      item.interestRatePct,
      item.maxLtvPct,
      item.liquidationThresholdPct,
      item.utilizationPct,
      item.tvlUsd,
      item.borrowedUsd,
      item.hasDdf ? 1 : 0,
    ]);
  });
  facilityStmt.free();

  const userStmt = db.prepare(`
    INSERT INTO users (id, name, wallet, email, investor_type, jurisdiction, kyc_status)
    VALUES (?, ?, ?, ?, ?, ?, ?)
  `);
  USERS.forEach((item) => {
    userStmt.run([
      item.id,
      item.name,
      item.wallet,
      item.email,
      item.investorType,
      item.jurisdiction,
      item.kycStatus,
    ]);
  });
  userStmt.free();

  const accountStmt = db.prepare(`
    INSERT INTO margin_accounts (
      id, facility_id, user_id, wallet, status, settlement_token, collateral_value_usd,
      borrowed_usd, interest_accrued, health_factor, last_payment, next_payment
    ) VALUES (?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?)
  `);
  MARGIN_ACCOUNTS.forEach((item) => {
    accountStmt.run([
      item.id,
      item.facilityId,
      item.userId,
      item.wallet,
      item.status,
      item.settlementToken,
      item.collateralValueUsd,
      item.borrowedUsd,
      item.interestAccrued,
      item.healthFactor,
      item.lastPayment,
      item.nextPayment,
    ]);
  });
  accountStmt.free();

  const collateralStmt = db.prepare(`
    INSERT INTO margin_account_collateral (id, margin_account_id, token_symbol, amount)
    VALUES (?, ?, ?, ?)
  `);
  MARGIN_ACCOUNT_COLLATERAL.forEach((item) => {
    collateralStmt.run([item.id, item.marginAccountId, item.tokenSymbol, item.amount]);
  });
  collateralStmt.free();

  const kycStmt = db.prepare(`
    INSERT INTO kyc_cases (id, user_id, facility_id, provider, submitted_date, risk_score, review_status)
    VALUES (?, ?, ?, ?, ?, ?, ?)
  `);
  KYC_CASES.forEach((item) => {
    kycStmt.run([
      item.id,
      item.userId,
      item.facilityId,
      item.provider,
      item.submittedDate,
      item.riskScore,
      item.reviewStatus,
    ]);
  });
  kycStmt.free();

  const liquidationStmt = db.prepare(`
    INSERT INTO liquidations (
      id, facility_id, margin_account_id, wallet, workflow_status,
      debt_amount_usd, collateral_amount_usd, health_factor, time_in_default,
      executed_at, recovery_rate_pct
    ) VALUES (?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?)
  `);
  LIQUIDATIONS.forEach((item) => {
    liquidationStmt.run([
      item.id,
      item.facilityId,
      item.marginAccountId,
      item.wallet,
      item.workflowStatus,
      item.debtAmountUsd,
      item.collateralAmountUsd,
      item.healthFactor,
      item.timeInDefault,
      item.executedAt,
      item.recoveryRatePct,
    ]);
  });
  liquidationStmt.free();

  const auctionStmt = db.prepare(`
    INSERT INTO auctions (
      id, facility_id, margin_account_id, title, status, token_symbol, token_name,
      collateral_amount, collateral_value_usd, debt_amount_usd, reserve_price_usd,
      current_bid_usd, bid_count, time_remaining, settlement_token
    ) VALUES (?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?)
  `);
  AUCTIONS.forEach((item) => {
    auctionStmt.run([
      item.id,
      item.facilityId,
      item.marginAccountId,
      item.title,
      item.status,
      item.tokenSymbol,
      item.tokenName,
      item.collateralAmount,
      item.collateralValueUsd,
      item.debtAmountUsd,
      item.reservePriceUsd,
      item.currentBidUsd,
      item.bidCount,
      item.timeRemaining,
      item.settlementToken,
    ]);
  });
  auctionStmt.free();

  const bidStmt = db.prepare(`
    INSERT INTO auction_bids (id, auction_id, bidder_wallet, amount_usd, timestamp, tx_hash, is_leading)
    VALUES (?, ?, ?, ?, ?, ?, ?)
  `);
  AUCTION_BIDS.forEach((item) => {
    bidStmt.run([
      item.id,
      item.auctionId,
      item.bidderWallet,
      item.amountUsd,
      item.timestamp,
      item.txHash,
      item.isLeading ? 1 : 0,
    ]);
  });
  bidStmt.free();

  const reqStmt = db.prepare(`
    INSERT INTO facility_requirements (id, facility_id, requirement_type, claim_type, description)
    VALUES (?, ?, ?, ?, ?)
  `);
  FACILITY_REQUIREMENTS.forEach((item) => {
    reqStmt.run([item.id, item.facilityId, item.requirementType, item.claimType, item.description]);
  });
  reqStmt.free();

  const onchainIdStmt = db.prepare(`
    INSERT INTO onchain_ids (id, user_id, address, source)
    VALUES (?, ?, ?, ?)
  `);
  ONCHAIN_IDS.forEach((item) => {
    onchainIdStmt.run([item.id, item.userId, item.address, item.source]);
  });
  onchainIdStmt.free();

  const onchainClaimStmt = db.prepare(`
    INSERT INTO onchain_claims (id, onchain_id, claim_type, issuer, value, issued_at)
    VALUES (?, ?, ?, ?, ?, ?)
  `);
  ONCHAIN_CLAIMS.forEach((item) => {
    onchainClaimStmt.run([item.id, item.onchainId, item.claimType, item.issuer, item.value, item.issuedAt]);
  });
  onchainClaimStmt.free();

  const onchainClaimReviewStmt = db.prepare(`
    INSERT INTO onchain_claim_review_cases (
      id, facility_id, user_id, onchain_id, claim_type, provider, issuer,
      jurisdiction, submitted_date, risk_score, review_status
    ) VALUES (?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?)
  `);
  ONCHAIN_CLAIM_REVIEW_CASES.forEach((item) => {
    onchainClaimReviewStmt.run([
      item.id,
      item.facilityId,
      item.userId,
      item.onchainId,
      item.claimType,
      item.provider,
      item.issuer,
      item.jurisdiction,
      item.submittedDate,
      item.riskScore,
      item.reviewStatus,
    ]);
  });
  onchainClaimReviewStmt.free();

  const accessStmt = db.prepare(`
    INSERT INTO user_facility_access (user_id, facility_id)
    VALUES (?, ?)
  `);
  USER_FACILITY_ACCESS.forEach((item) => {
    accessStmt.run([item.userId, item.facilityId]);
  });
  accessStmt.free();

  const poolStmt = db.prepare(`
    INSERT INTO lending_pools (id, facility_id, name, tvl_usd, utilization_pct, apy_pct)
    VALUES (?, ?, ?, ?, ?, ?)
  `);
  LENDING_POOLS.forEach((item) => {
    poolStmt.run([item.id, item.facilityId, item.name, item.tvlUsd, item.utilizationPct, item.apyPct]);
  });
  poolStmt.free();

  const posStmt = db.prepare(`
    INSERT INTO lender_positions (id, pool_id, user_id, deposited_usd, lp_tokens, earned_yield_usd, withdrawable_pct)
    VALUES (?, ?, ?, ?, ?, ?, ?)
  `);
  LENDER_POSITIONS.forEach((item) => {
    posStmt.run([item.id, item.poolId, item.userId, item.depositedUsd, item.lpTokens, item.earnedYieldUsd, item.withdrawablePct]);
  });
  posStmt.free();

  const iouTokenStmt = db.prepare(`
    INSERT INTO ious (id, pool_id, facility_name, original_amount_usd, repaid_amount_usd, remaining_amount_usd, status)
    VALUES (?, ?, ?, ?, ?, ?, ?)
  `);
  IOUS.forEach((item) => {
    iouTokenStmt.run([item.id, item.poolId, item.facilityName, item.originalAmountUsd, item.repaidAmountUsd, item.remainingAmountUsd, item.status]);
  });
  iouTokenStmt.free();

  const iouEventStmt = db.prepare(`
    INSERT INTO iou_events (id, iou_id, pool_id, event_type, amount_usd, created_at)
    VALUES (?, ?, ?, ?, ?, ?)
  `);
  IOU_EVENTS.forEach((item) => {
    iouEventStmt.run([item.id, item.iouId, item.poolId, item.eventType, item.amountUsd, item.createdAt]);
  });
  iouEventStmt.free();

  const yieldStmt = db.prepare(`
    INSERT INTO yield_history (id, pool_id, user_id, week_label, yield_usd, cumulative_usd)
    VALUES (?, ?, ?, ?, ?, ?)
  `);
  YIELD_HISTORY.forEach((item) => {
    yieldStmt.run([item.id, item.poolId, item.userId, item.weekLabel, item.yieldUsd, item.cumulativeUsd]);
  });
  yieldStmt.free();

  const moduleStmt = db.prepare(`
    INSERT INTO credit_facility_modules (id, module_type, name, description, chain_address, chain_id, updated_at)
    VALUES (?, ?, ?, ?, ?, ?, ?)
  `);
  CF_MODULE_WHITELIST.forEach((item) => {
    moduleStmt.run([item.id, item.moduleType, item.name, item.description, item.chainAddress, item.chainId, item.updatedAt]);
  });
  moduleStmt.free();

  const facilityModuleStmt = db.prepare(`
    INSERT INTO credit_facilities_to_modules (id, facility_id, module_id)
    VALUES (?, ?, ?)
  `);
  CF_FACILITY_MODULES.forEach((item) => {
    facilityModuleStmt.run([item.id, item.facilityId, item.moduleId]);
  });
  facilityModuleStmt.free();
}

async function buildDatabase(): Promise<Database> {
  const SQL = await initSqlJs({ locateFile: () => wasmUrl });
  const db = new SQL.Database();
  createSchema(db);
  seedData(db);
  return db;
}

export async function initMockDb(): Promise<void> {
  if (!initPromise) {
    initPromise = buildDatabase().then((db) => {
      dbInstance = db;
      return db;
    });
  }
  await initPromise;
}

export async function getDb(): Promise<Database> {
  if (dbInstance) {
    return dbInstance;
  }
  await initMockDb();
  if (!dbInstance) {
    throw new Error('Mock database initialization failed');
  }
  return dbInstance;
}

export function resetMockDbForTests(): void {
  if (dbInstance) {
    dbInstance.close();
  }
  dbInstance = null;
  initPromise = null;
}
